import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import './Modal.css'

/**
 * Reusable Modal Component
 *
 * A flexible modal dialog with customizable header, body, and footer
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Modal visibility state
 * @param {Function} props.onClose - Close handler
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal body content
 * @param {React.ReactNode} props.footer - Modal footer content
 * @param {string} props.size - Modal size (small, medium, large, xl)
 * @param {boolean} props.closeOnEscape - Close on Escape key (default: true)
 * @param {boolean} props.closeOnBackdrop - Close on backdrop click (default: true)
 * @param {boolean} props.showCloseButton - Show close button (default: true)
 * @param {string} props.className - Additional CSS classes
 */
const Modal = ({
	isOpen,
	onClose,
	title,
	children,
	footer,
	size = 'medium',
	closeOnEscape = true,
	closeOnBackdrop = true,
	showCloseButton = true,
	className = '',
}) => {
	/**
	 * Handle Escape key press
	 */
	useEffect(() => {
		if (!isOpen || !closeOnEscape) return

		const handleEscape = (e) => {
			if (e.key === 'Escape') {
				onClose()
			}
		}

		document.addEventListener('keydown', handleEscape)
		return () => document.removeEventListener('keydown', handleEscape)
	}, [isOpen, closeOnEscape, onClose])

	/**
	 * Prevent body scroll when modal is open
	 */
	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}

		return () => {
			document.body.style.overflow = ''
		}
	}, [isOpen])

	/**
	 * Handle backdrop click
	 */
	const handleBackdropClick = (e) => {
		if (closeOnBackdrop && e.target === e.currentTarget) {
			onClose()
		}
	}

	if (!isOpen) return null

	const modalClasses = ['modal', `modal--${size}`, className].filter(Boolean).join(' ')

	// Use createPortal to render modal at document body level
	// This ensures position:fixed works correctly even inside transformed parents
	return createPortal(
		<div className="modal-overlay" onClick={handleBackdropClick}>
			<div className={modalClasses} role="dialog" aria-modal="true" aria-labelledby="modal-title">
				{/* Header */}
				<div className="modal__header">
					{title && (
						<h2 id="modal-title" className="modal__title">
							{title}
						</h2>
					)}
					{showCloseButton && (
						<button
							className="modal__close"
							onClick={onClose}
							aria-label="Close modal"
						>
							<X size={20} />
						</button>
					)}
				</div>

				{/* Body */}
				<div className="modal__body">{children}</div>

				{/* Footer */}
				{footer && <div className="modal__footer">{footer}</div>}
			</div>
		</div>,
		document.body
	)
}

export default Modal
